'use client'

/**
 * Inert links for email (or page) HTML shown in a FRAME on an admin screen —
 * an <iframe srcdoc> whose document is the email.
 *
 * Inline previews make their links inert with `useInertLinks` (same folder),
 * which catches the click in the page. A frame is different: its document is
 * not the page's, and a frame sandboxed with `sandbox=""` is an opaque origin
 * the page cannot reach at all. The sandbox already stops a link leaving the
 * product (no pop-ups, no top navigation), but not the frame navigating
 * ITSELF: a click, a middle click or Enter on a link loaded the link's address
 * into the frame. The browser contacted that domain — a lure domain someone may
 * own — and the preview went blank (follow-up to the P2 of 2026-10-06). On the
 * PhishTrainer invite the link is the learner's single-use sign-in, so a click
 * redeemed it.
 *
 * So the HTML itself changes, before it becomes the frame's document:
 *
 *   - Every link (`<a>`, `<area>`, SVG `<a>`) loses its `href` (and SVG's
 *     `xlink:href`); the address moves to `data-href` (`data-xlink-href`),
 *     exactly as written. With no href nothing navigates — mouse, middle click,
 *     keyboard — in any frame, scripts or not. mailto:, tel: and "#" links are
 *     treated the same: mailto/tel would hand a lure's address to the mail or
 *     phone app, and "#" is not an in-page jump in a srcdoc frame (its base URL
 *     is the parent's, so it loaded the app into the frame).
 *   - The link gets a `title` naming the address (`previewLinks.disabledTitle`,
 *     the same words the inline previews use), `aria-disabled="true"` and
 *     `role="link"`, so hovering shows where it points and assistive technology
 *     still reads a link, now unavailable. These go FIRST in the tag: the
 *     browser keeps the first of two same-named attributes, so a title the
 *     email set to hide its real destination is ignored.
 *   - One small style keeps a link without an href looking like a link
 *     (colour, underline). It sits in `:where()`, zero specificity, so any
 *     rule the email writes for its links still wins, and inline styles always
 *     do. It is added only when a link was changed.
 *
 * Nothing else changes, byte for byte: the attributes are renamed in place and
 * HTML with no link comes back as it went in.
 *
 * How links are found. The HTML is scanned with the browser's own tokenizer
 * rules (start tags, attribute quoting, comments, doctype, and the elements
 * whose content is text: script, style, textarea, title, …) — a string scan,
 * not a DOM round trip, so the output is identical on the server and in the
 * browser (no hydration mismatch) and nothing is re-serialised. `<noscript>` is
 * scanned as markup on purpose: these frames run no script, so its content is
 * shown and clickable. Then, in the browser, the result is parsed with
 * DOMParser — the same parser the frame uses — and if anything followable is
 * left (markup a string scan cannot judge without building the tree, such as a
 * `<style>` inside `<svg>`, whose content IS markup), every `href` /
 * `xlink:href` attribute name in the document is renamed instead. That last
 * step is blunt (it also renames a stylesheet `<link href>`), which is why it
 * only runs when the precise one was not enough. Without a DOM (a server render)
 * the scan result is used as is.
 *
 * Not covered, deliberately: images and stylesheets still load (a different
 * question, answered per surface — see ESA's frame CSP), and
 * `<meta http-equiv="refresh">` is not a link.
 */
import { useCallback } from 'react'
import { useTranslation } from '@/lib/i18n'

type Translate = (key: string, params?: Record<string, string | number>) => string

/** Marks the injected style. */
export const INERT_LINKS_STYLE_MARKER = 'data-hf-inert-links'

/** Keeps a link without an href looking like a link; the email's own CSS still wins. */
export const INERT_LINKS_STYLE =
  `<style ${INERT_LINKS_STYLE_MARKER}>` +
  ':where(a[data-href]){color:#0000ee;color:LinkText;text-decoration:underline;cursor:not-allowed}' +
  '</style>'

/** The attribute each link's address moves to. */
const MOVED: Readonly<Record<string, string>> = { href: 'data-href', 'xlink:href': 'data-xlink-href' }

/** Elements whose content the tokenizer reads as text (HTML namespace). */
const TEXT_CONTENT = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes'])

/** Stands in for the address while the title is translated. */
const URL_SLOT = ''

const XLINK_NS = 'http://www.w3.org/1999/xlink'

interface Attribute {
  name: string
  /** Where its name starts and ends in the source. */
  start: number
  nameEnd: number
  /** The value as written (entities undecoded), or null for a bare attribute. */
  value: string | null
}

interface StartTag {
  name: string
  nameEnd: number
  attrs: Attribute[]
  /** Just past its closing `>`. */
  end: number
}

const isSpace = (c: string | undefined) => c === ' ' || c === '\n' || c === '\t' || c === '\f' || c === '\r'
const isAsciiAlpha = (c: string | undefined) => c !== undefined && ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z'))
/** ASCII-only lower case, as the tokenizer does — and it keeps every index where it was. */
const asciiLower = (s: string) => s.replace(/[A-Z]+/g, (m) => m.toLowerCase())

/**
 * Read a tag from its name (just after `<` or `</`) to its `>`, following the
 * HTML tokenizer's states. Null when the input ends inside the tag — the
 * browser drops such a tag.
 */
function readTag(html: string, from: number): StartTag | null {
  const n = html.length
  let i = from
  while (i < n && !isSpace(html[i]) && html[i] !== '/' && html[i] !== '>') i++
  const tag: StartTag = { name: asciiLower(html.slice(from, i)), nameEnd: i, attrs: [], end: -1 }
  while (i < n) {
    const c = html[i]
    // Whitespace and a "/" not followed by ">" both just separate attributes.
    if (isSpace(c) || c === '/') {
      i++
      continue
    }
    if (c === '>') {
      tag.end = i + 1
      return tag
    }
    const start = i
    i++ // the first character is part of the name, even "="
    while (i < n && !isSpace(html[i]) && html[i] !== '/' && html[i] !== '>' && html[i] !== '=') i++
    const attr: Attribute = { name: asciiLower(html.slice(start, i)), start, nameEnd: i, value: null }
    tag.attrs.push(attr)
    let j = i
    while (j < n && isSpace(html[j])) j++
    if (html[j] !== '=') {
      i = j
      continue
    }
    j++
    while (j < n && isSpace(html[j])) j++
    if (j >= n) return null
    const q = html[j]
    if (q === '"' || q === "'") {
      const close = html.indexOf(q, j + 1)
      if (close === -1) return null
      attr.value = html.slice(j + 1, close)
      i = close + 1
    } else if (q === '>') {
      attr.value = ''
      i = j
    } else {
      let k = j
      while (k < n && !isSpace(html[k]) && html[k] !== '>') k++
      attr.value = html.slice(j, k)
      i = k
    }
  }
  return null
}

/** Just past the end of a comment whose `<!--` ends at `from`, or -1 if it never ends. */
function commentEnd(html: string, from: number): number {
  if (html[from] === '>') return from + 1
  if (html[from] === '-' && html[from + 1] === '>') return from + 2
  const dashes = html.indexOf('-->', from)
  const bang = html.indexOf('--!>', from)
  if (dashes === -1 && bang === -1) return -1
  return bang === -1 || (dashes !== -1 && dashes < bang) ? dashes + 3 : bang + 4
}

/** Where the text content of `name` ends (its `</name`), or -1 if it runs to the end. */
function textContentEnd(lower: string, name: string, from: number): number {
  const close = `</${name}`
  for (let at = lower.indexOf(close, from); at !== -1; at = lower.indexOf(close, at + 1)) {
    const next = lower[at + close.length]
    if (next === undefined || next === '/' || next === '>' || isSpace(next)) return at
  }
  return -1
}

interface Scan {
  links: Array<{ tag: StartTag; hrefs: Attribute[] }>
  /** Where the style goes: after <head>, else <html>, else the doctype, else the start. */
  styleAt: number
}

function scan(html: string): Scan {
  const n = html.length
  const lower = asciiLower(html)
  const links: Scan['links'] = []
  let head = -1
  let root = -1
  let doctype = -1
  let i = 0
  while (i < n) {
    const lt = html.indexOf('<', i)
    if (lt === -1) break
    const c = html[lt + 1]
    if (isAsciiAlpha(c)) {
      const tag = readTag(html, lt + 1)
      if (!tag) break
      if (tag.name === 'a' || tag.name === 'area') {
        const hrefs = tag.attrs.filter((a) => a.name in MOVED)
        if (hrefs.length > 0) links.push({ tag, hrefs })
      } else if (tag.name === 'head' && head === -1) head = tag.end
      else if (tag.name === 'html' && root === -1) root = tag.end
      i = tag.end
      if (tag.name === 'plaintext') break
      if (TEXT_CONTENT.has(tag.name)) {
        const end = textContentEnd(lower, tag.name, i)
        if (end === -1) break
        i = end
      }
      continue
    }
    if (c === '/') {
      if (isAsciiAlpha(html[lt + 2])) {
        const tag = readTag(html, lt + 2)
        if (!tag) break
        i = tag.end
        continue
      }
      if (html[lt + 2] === '>') {
        i = lt + 3
        continue
      }
    }
    if (c === '!' && html.startsWith('<!--', lt)) {
      const end = commentEnd(html, lt + 4)
      if (end === -1) break
      i = end
      continue
    }
    if (c === '!' || c === '?' || c === '/') {
      // Doctype, bogus comment, or an end tag that is not one: up to the next ">".
      const gt = html.indexOf('>', lt + 2)
      if (gt === -1) break
      if (doctype === -1 && lower.startsWith('<!doctype', lt)) doctype = gt + 1
      i = gt + 1
      continue
    }
    i = lt + 1
  }
  return { links, styleAt: head !== -1 ? head : root !== -1 ? root : doctype !== -1 ? doctype : 0 }
}

const escapeAttribute = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

/**
 * The title, double-quoted. The address goes in exactly as written in the href
 * (only a `"` is escaped), so the browser decodes it the same way it decoded
 * the link — `&amp;` reads as `&` in both.
 */
function titleFor(value: string | null, t: Translate): string {
  const text = t('previewLinks.disabledTitle', { url: URL_SLOT })
  const url = (value ?? '').replace(/"/g, '&quot;')
  const at = text.indexOf(URL_SLOT)
  if (at === -1) return `${escapeAttribute(text)}\n${url}`
  return escapeAttribute(text.slice(0, at)) + url + escapeAttribute(text.slice(at + URL_SLOT.length))
}

/** Does `root` (and every template in it) still hold something the browser would follow? */
function holdsFollowableLink(root: ParentNode): boolean {
  for (const el of Array.from(root.querySelectorAll('a, area, math [href], math[href]'))) {
    if (el.hasAttribute('href') || el.hasAttributeNS(XLINK_NS, 'href')) return true
  }
  for (const template of Array.from(root.querySelectorAll('template'))) {
    if (holdsFollowableLink((template as HTMLTemplateElement).content)) return true
  }
  return false
}

/** The browser's verdict, when there is a browser. Fails closed. */
function browserWouldFollowSomething(html: string): boolean {
  if (typeof DOMParser === 'undefined' || !/href/i.test(html)) return false
  try {
    return holdsFollowableLink(new DOMParser().parseFromString(html, 'text/html'))
  } catch {
    return true
  }
}

/**
 * Every attribute name the tokenizer can produce starts after whitespace, "/"
 * or a closing quote, so this renames every href / xlink:href the document
 * holds, whatever element it is on.
 */
const ANY_HREF_ATTRIBUTE = /(^|[\t\n\f\r "'/])((?:xlink:)?href)(?=[\t\n\f\r />=]|$)/gi

/** Take every link's href away, keeping its look and naming its address on hover. */
export function inertLinksHtml(html: string, t: Translate): string {
  if (!html) return html
  const { links, styleAt } = scan(html)
  const edits: Array<{ from: number; to: number; text: string }> = []
  for (const { tag, hrefs } of links) {
    const shown = hrefs.find((a) => a.name === 'href') ?? hrefs[0]
    edits.push({
      from: tag.nameEnd,
      to: tag.nameEnd,
      text: ` title="${titleFor(shown.value, t)}" aria-disabled="true" role="link"`,
    })
    for (const a of hrefs) edits.push({ from: a.start, to: a.nameEnd, text: MOVED[a.name] })
  }
  if (edits.length > 0) edits.push({ from: styleAt, to: styleAt, text: INERT_LINKS_STYLE })
  edits.sort((x, y) => x.from - y.from)

  let out = ''
  let at = 0
  for (const edit of edits) {
    out += html.slice(at, edit.from) + edit.text
    at = edit.to
  }
  out += html.slice(at)

  return browserWouldFollowSomething(out) ? out.replace(ANY_HREF_ATTRIBUTE, '$1data-$2') : out
}

/** How many recent documents are remembered per language. */
const REMEMBERED = 8
const remembered = new WeakMap<Translate, Map<string, string>>()

/** `inertLinksHtml`, memoised: the same HTML in the same language gives the same answer. */
function rememberedInertLinksHtml(html: string, t: Translate): string {
  let byHtml = remembered.get(t)
  if (!byHtml) {
    byHtml = new Map()
    remembered.set(t, byHtml)
  }
  const known = byHtml.get(html)
  if (known !== undefined) return known
  const out = inertLinksHtml(html, t)
  byHtml.set(html, out)
  if (byHtml.size > REMEMBERED) byHtml.delete(byHtml.keys().next().value as string)
  return out
}

/**
 * `inertLinksHtml` in the admin's language, for a component that renders a
 * frame. Bind it once — `const inertLinks = useInertLinksHtml()` — and make it
 * the OUTERMOST call of the frame's document: the frame's srcdoc prop is
 * `inertLinks(…)`, with every other transform inside the parentheses, so
 * nothing applied afterwards can bring a link back.
 * lib/__tests__/rendered-email-links-are-inert.guard.spec.ts holds every frame
 * to that shape. Recent answers are remembered, so a re-render with the same
 * HTML (a keystroke elsewhere in a dialog) does no work.
 */
export function useInertLinksHtml(): (html: string) => string {
  const { t } = useTranslation()
  return useCallback((html: string) => rememberedInertLinksHtml(html, t), [t])
}
